import { formatDisplayAmount } from '@imprint-payments/imprint-lib';
import {
  colors,
  Icons,
  ImpAccordionItemProps,
  ImpBulletList,
  ImpInlineLink,
  ImpListItemProps,
  ImpOfferPageProps,
  ImpSpacer,
  ImpText,
} from '@imprint-payments/imprint-ui';
import { useState } from 'react';

import { EVENT } from '../../analytics/analyticsConsts';
import { confirm } from '../../api/api';
import { URL } from '../../api/apiConstants';
import { setRedirectToLogin } from '../../app/acceptScreenSlice';
import { ConsumerInfo } from '../../app/consumerInfoSlice';
import { useAppSelector, useImpState } from '../../app/hooks';
import { SCREEN, setScreen } from '../../app/screenStateSlice';
import { type LandingPageURLKey } from '../../assets/consts/inStoreConsts';
import { ANALYTICS } from '../../firebase/firebase';
import { getEmployeeId, getStoreNumber } from '../../lib/utmManager';
import { displayNumberNone, formatRewardsBonus } from '../../utils/formatUtils';
import {
  clearHomePageState,
  getOfferUUIDs,
  redirectToAccountWithCookie,
} from '../../utils/functionUtils';
import { LanguageCode } from '../../utils/language';
import { merchantInfoByProductUUID } from '../../utils/productUUIDConstants';
import { PAGE } from '../../utils/routeConstant';
import {
  emitSDKEvent,
  isSDK,
  SDK_EVENTS,
  SDK_RESULTS,
  useSDKContext,
} from '../../utils/sdk';
import { navigateToLP } from '../application/InStoreModal';

const getFeeTableData = (
  consumerInfo: ConsumerInfo,
  language: any
): ImpListItemProps[] => {
  return [
    {
      title: language.annualFee,
      trailingSubtitle: displayNumberNone(
        consumerInfo.cardOffer.fees.annualFee,
        language
      ),
    },
    {
      title: language.overlimitFee,
      trailingSubtitle: displayNumberNone(
        consumerInfo.cardOffer.fees.overlimitFee,
        language
      ),
    },
    {
      title: language.internationalFee,
      trailingSubtitle: displayNumberNone(
        consumerInfo.cardOffer.fees.internationalFee,
        language
      ),
    },
    {
      title: language.lateFee,
      trailingSubtitle: `${language.upto} ${displayNumberNone(
        consumerInfo.cardOffer.fees?.lateFee?.additionalTime,
        language
      )}`,
    },
    {
      title: language.returnedFee,
      trailingSubtitle: `${language.upto} ${displayNumberNone(
        consumerInfo.cardOffer.fees?.returnedPaymentFee?.additionalTime,
        language
      )}`,
    },
  ];
};

const useRewardsTableData = (): ImpAccordionItemProps[] => {
  const { merchantData, language } = useImpState();
  const offerData: ImpAccordionItemProps[] = [];

  if (merchantData.rewardDetails.rewardsBonus) {
    const offerUUIDsSet = new Set(getOfferUUIDs());
    for (let i = 0; i < merchantData.rewardDetails.rewardsBonus.length; i++) {
      const rewardsBonus = merchantData.rewardDetails.rewardsBonus[i];
      if (rewardsBonus.offerConfigUUIDs) {
        for (let j = 0; j < rewardsBonus.offerConfigUUIDs.length; j++) {
          const offerUUID = rewardsBonus.offerConfigUUIDs[j];
          if (
            (offerUUIDsSet.size === 0 && offerUUID === 'default') ||
            offerUUIDsSet.has(offerUUID)
          ) {
            offerData.push({
              title: rewardsBonus.title,
              subtitle: formatRewardsBonus(rewardsBonus),
              content: rewardsBonus.items.text ? (
                <div
                  dangerouslySetInnerHTML={{ __html: rewardsBonus.items.text }}
                  style={{
                    color: colors.content.onSurfaceVariant,
                  }}
                />
              ) : undefined,
              badge: {
                leadingIcon: Icons.timer,
                children: language.limitedTimeOfferBadge,
                color: 'var(--merchant-color)',
                variant: 'primary',
              },
            });
            break;
          }
        }
      } else {
        offerData.push({
          title: rewardsBonus.title,
          subtitle: formatRewardsBonus(rewardsBonus),
          content: rewardsBonus.items.text ? (
            <div
              dangerouslySetInnerHTML={{ __html: rewardsBonus.items.text }}
              style={{
                color: colors.content.onSurfaceVariant,
              }}
            />
          ) : undefined,
        });
      }
    }
  }
  return offerData;
};

const getDisclosureLinks = (
  productUUID: string,
  languageCode: LanguageCode
) => {
  return {
    rewardsTermsAndConditions:
      merchantInfoByProductUUID[productUUID].rewardsTerms[languageCode],
    cca: merchantInfoByProductUUID[productUUID].cardholderAgreement[
      languageCode
    ],
  };
};

export const useOfferPageProps = ({
  sdkSecondaryAction,
}: {
  sdkSecondaryAction?: () => void;
  setOpenInStoreModal?: () => void;
}): ImpOfferPageProps => {
  const [pageState, setPageState] = useState<
    'submitting' | 'enabled' | 'disabled'
  >('enabled');
  const rewardsListItems = useRewardsTableData();
  const {
    appScreenState,
    acceptScreenState,
    appStatus,
    consumerInfo,
    language,
    languageCode,
    merchantData,
    dispatch,
    navigate,
    apiScreenState,
  } = useImpState();

  const { setResult } = useSDKContext();

  const multiCardUser = useAppSelector(
    (state) => state.loginSelector.multiCardUser
  );
  const redirectToLogin = () => {
    dispatch(setRedirectToLogin(true));
    redirectToAccountWithCookie(
      consumerInfo.auth.token,
      apiScreenState.deviceID,
      consumerInfo.consumerUUID,
      merchantData.productUUID,
      multiCardUser === true ? 'true' : 'false',
      true
    );
  };
  const inStoreOnboarding =
    getEmployeeId(merchantData.productUUID) ||
    getStoreNumber(merchantData.productUUID);
  const { rewardsTermsAndConditions, cca } = getDisclosureLinks(
    merchantData.productUUID,
    languageCode
  );

  const hasAcceptedOffer = Boolean(appStatus.productAccountUUID);

  const gotoOnboardingFlow = () => {
    navigate(PAGE.ONBOARDING);
  };

  const handlePrimaryAction = () => {
    setPageState('submitting');

    if (hasAcceptedOffer || acceptScreenState.cardDesignSelection) {
      ANALYTICS.logEvent(EVENT.OFFER_PAGE_CONTINUE_CLICKED);
      if (isSDK()) {
        gotoOnboardingFlow();
        return;
      }
      dispatch(setScreen(SCREEN.ACCEPT));
      navigate(URL.ACCEPT);
    } else {
      // accept offer
      ANALYTICS.logEvent(EVENT.OFFER_PAGE_ACCEPT_OFFER_CLICKED);
      dispatch(
        confirm({
          token: consumerInfo.auth.token,
          applicationUUID: consumerInfo.auth.applicationUUID,
          sendSms: appScreenState.inStoreDevice,
          onSuccess: () => {
            setResult(SDK_RESULTS.OFFER_ACCEPTED);
            emitSDKEvent({
              event: SDK_EVENTS.OFFER_ACCEPTED,
            });

            if (appScreenState.inStoreDevice) {
              dispatch(setScreen(SCREEN.CREATE_PIN));
              navigate(PAGE.CREATE_PIN);
            } else if (inStoreOnboarding) redirectToLogin();
            else {
              if (isSDK()) {
                gotoOnboardingFlow();
                return;
              }

              dispatch(setScreen(SCREEN.ACCEPT));
              navigate(URL.ACCEPT);
            }
          },
        })
      );
    }
  };

  const handleSecondaryAction = () => {
    ANALYTICS.logEvent(EVENT.OFFER_NO_THANKS_CLICKED);

    if (isSDK()) {
      if (sdkSecondaryAction) {
        sdkSecondaryAction();
      } else {
        emitSDKEvent({
          event: SDK_EVENTS.CLOSED,
        });
      }
    }
  };

  const handleLeaveOfferClick = (
    inStoreDevice: boolean | undefined,
    productUUID: string
  ) => {
    clearHomePageState(dispatch);
    if (inStoreDevice) {
      navigateToLP(productUUID as LandingPageURLKey);
    } else {
      navigate(PAGE.HOME);
    }
  };

  function getPrimaryButtonLabel() {
    if (hasAcceptedOffer) {
      // User has already accepted the offer; show a different button label if they navigated back to this screen
      return language.continueBtn;
    } else if (acceptScreenState.cardDesignSelection) {
      // if merchant has card design selection, wording and action change
      return language.acceptAndContinue;
    } else {
      return language.acceptOfferBtn;
    }
  }

  const offerPageProps = {
    display: {
      title: `${language.offerIntro} ${merchantData.rewardDetails.cardName}`,
      displayImage: merchantData.cardOfferImg,
      pageState,
    },
    info: {
      creditLimit: {
        title: formatDisplayAmount(consumerInfo.cardOffer.limit),
        subtitle: language.creditLimit,
        showIcon: true,
      },
      secondHeaderItem: {
        title: formatDisplayAmount(consumerInfo.cardOffer.apr, true),
        subtitle: language.apr,
        showIcon: true,
      },
      rewardsList: {
        title: language.yourRewards,
        items: rewardsListItems,
      },
      feesList: {
        title: language.fees + '<sup>1</sup>',
        items: getFeeTableData(consumerInfo, language),
      },
      actionDisclaimer: hasAcceptedOffer
        ? language.alreadyAcceptedOffer
        : language.affectCreditScoreStr,
      feesDisclosure: (
        <>
          <ImpSpacer height="m" />
          <ImpBulletList
            items={[
              <ImpText
                typography="body3"
                color={colors.content.onSurfaceVariant}
                key="offer-disclosure-text"
              >
                {language.offerDisclosure1}
                <ImpInlineLink
                  url={rewardsTermsAndConditions}
                  color="var(--merchant-color)"
                >
                  {language.offerDisclosure2}
                </ImpInlineLink>
                {language.offerDisclosure3}
                <ImpInlineLink
                  url={consumerInfo.cardOffer.cardholderAgreementURL || cca}
                  color="var(--merchant-color)"
                >
                  {language.offerDisclosure4}
                </ImpInlineLink>
                {language.offerDisclosure5}
              </ImpText>,
            ]}
            type="numeric"
            bulletStyle={{
              color: colors.content.onSurfaceVariant,
              typography: 'body3',
            }}
          />
        </>
      ),
    },
    modals: {
      edu: {
        creditLimitTitle: language.creditLimit,
        creditLimitSubtitle: language.creditLimitDefinition,
        aprTitle: language.apr,
        aprSubtitle: language.aprDefinition,
      },
      rejectionModal: {
        title: language.rejectOfferTitle,
        subtitle: language.rejectOfferSubtitle.replace(
          '${1}',
          appStatus.expiresAt
        ),
        primaryButton: {
          text: language.rejectOfferPrimaryButtonText,
          // onclick is managed by the modal within imprint-ui
          onClick: () => {},
        },
        secondaryButton: {
          text: language.rejectOfferSecondaryButtonText,
          onClick: () =>
            handleLeaveOfferClick(
              appScreenState.inStoreDevice,
              merchantData.productUUID
            ),
        },
      },
    },
    actions: {
      // if merchant has card design selection, wording and action change
      primaryButton: {
        text: getPrimaryButtonLabel(),
        onClick: handlePrimaryAction,
      },
      ...(!hasAcceptedOffer && {
        secondaryButton: {
          text: language.noThanksBtn,
          onClick: handleSecondaryAction,
        },
      }),
    },
  };

  return offerPageProps;
};
